import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import { isAdmin, isUser, isStaff } from '../../features/auth/authSlice';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import {
    UserIcon,
    BuildingOfficeIcon,
    BriefcaseIcon,
    ChartBarIcon,
    BellIcon,
    ExclamationTriangleIcon,
    ClockIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    AcademicCapIcon,
    ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

interface DashboardStats {
    users: {
        total: number;
        active: number;
        newThisMonth: number;
        growth: number;
    };
    companies: {
        total: number;
        active: number;
        newThisMonth: number;
        growth: number;
    };
    tasks: {
        total: number;
        active: number;
        completed: number;
        pending: number;
        growth: number;
    };
    inquiries: {
        total: number;
        pending: number;
        contacted: number;
        approved: number;
    };
    applications: {
        total: number;
        pending: number;
        approved: number;
        rejected: number;
    };
    notifications: {
        total: number;
        unread: number;
    };
}

interface RecentActivity {
    id: string;
    type: string;
    description: string;
    timestamp: string;
    user?: string;
    status?: string;
}

const AdminPanel: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { user } = useAppSelector((state) => state.auth);
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchDashboardData = useCallback(async () => {
        try {
            setLoading(true);

            // Fetch all data in parallel for better performance
            const [usersResponse, companiesResponse, tasksResponse, notificationsResponse, inquiriesResponse, dashboardStatsResponse] = await Promise.all([
                api.get('/users'),
                api.get('/companies'),
                api.get('/tasks'),
                api.get('/notifications'),
                api.get('/admin/company-inquiries'),
                api.get('/admin/dashboard-stats')
            ]);

            const users = usersResponse.data.data || [];
            const companies = companiesResponse.data.data || [];
            const tasks = tasksResponse.data.data?.tasks || [];
            const notifications = notificationsResponse.data.data || [];
            const inquiries = inquiriesResponse.data.data?.inquiries || [];
            const serverDashboardStats = dashboardStatsResponse.data.data || {};

            // Debug logging
            console.log('Dashboard data:', {
                users: users.length,
                companies: companies.length,
                tasks: tasks.length,
                notifications: notifications.length,
                inquiries: inquiries.length
            });

            // Calculate current month stats
            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();

            // User statistics
            const activeUsers = Array.isArray(users) ? users.filter((user: any) => user.isActive).length : 0;
            const newUsersThisMonth = Array.isArray(users) ? users.filter((user: any) => {
                const userDate = new Date(user.createdAt);
                return userDate.getMonth() === currentMonth && userDate.getFullYear() === currentYear;
            }).length : 0;

            // Company statistics
            const activeCompanies = Array.isArray(companies) ? companies.filter((company: any) => company.isActive).length : 0;
            const newCompaniesThisMonth = Array.isArray(companies) ? companies.filter((company: any) => {
                const companyDate = new Date(company.createdAt);
                return companyDate.getMonth() === currentMonth && companyDate.getFullYear() === currentYear;
            }).length : 0;

            // Task statistics
            const activeTasks = Array.isArray(tasks) ? tasks.filter((task: any) => task.status === 'active').length : 0;
            const completedTasks = Array.isArray(tasks) ? tasks.filter((task: any) => task.status === 'completed').length : 0;
            const pendingTasks = Array.isArray(tasks) ? tasks.filter((task: any) => task.status === 'pending').length : 0;

            // Notification statistics
            const unreadNotifications = Array.isArray(notifications) ? notifications.filter((notification: any) => !notification.isRead).length : 0;

            // Inquiry statistics
            const pendingInquiries = Array.isArray(inquiries) ? inquiries.filter((inquiry: any) => inquiry.status === 'pending').length : 0;
            const contactedInquiries = Array.isArray(inquiries) ? inquiries.filter((inquiry: any) => inquiry.status === 'contacted').length : 0;
            const approvedInquiries = Array.isArray(inquiries) ? inquiries.filter((inquiry: any) => inquiry.status === 'approved').length : 0;

            // Calculate growth percentages (simplified calculation)
            const userGrowth = Array.isArray(users) && users.length > 0 ? ((newUsersThisMonth / users.length) * 100) : 0;
            const companyGrowth = Array.isArray(companies) && companies.length > 0 ? ((newCompaniesThisMonth / companies.length) * 100) : 0;
            const taskGrowth = Array.isArray(tasks) && tasks.length > 0 ? ((activeTasks / tasks.length) * 100) : 0;

            // Prepare recent activity from notifications and other sources
            const activity: RecentActivity[] = [
                ...(Array.isArray(notifications) ? notifications.slice(0, 3).map((notification: any) => ({
                    id: notification._id,
                    type: 'notification',
                    description: notification.message,
                    timestamp: notification.createdAt,
                    user: notification.user ? `${notification.user.firstName} ${notification.user.lastName}` : 'System',
                    status: notification.isRead ? 'read' : 'unread'
                })) : []),
                ...(Array.isArray(inquiries) ? inquiries.slice(0, 2).map((inquiry: any) => ({
                    id: inquiry._id,
                    type: 'inquiry',
                    description: `New inquiry from ${inquiry.companyName}`,
                    timestamp: inquiry.createdAt,
                    status: inquiry.status
                })) : [])
            ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 5);

            const dashboardStats: DashboardStats = {
                users: {
                    total: serverDashboardStats.users?.total || (Array.isArray(users) ? users.length : 0),
                    active: serverDashboardStats.users?.active || activeUsers,
                    newThisMonth: newUsersThisMonth,
                    growth: userGrowth
                },
                companies: {
                    total: serverDashboardStats.companies?.total || (Array.isArray(companies) ? companies.length : 0),
                    active: serverDashboardStats.companies?.active || activeCompanies,
                    newThisMonth: newCompaniesThisMonth,
                    growth: companyGrowth
                },
                tasks: {
                    total: serverDashboardStats.tasks?.total || (Array.isArray(tasks) ? tasks.length : 0),
                    active: serverDashboardStats.tasks?.active || activeTasks,
                    completed: serverDashboardStats.tasks?.completed || completedTasks,
                    pending: serverDashboardStats.tasks?.pending || pendingTasks,
                    growth: taskGrowth
                },
                inquiries: {
                    total: serverDashboardStats.inquiries?.total || (Array.isArray(inquiries) ? inquiries.length : 0),
                    pending: serverDashboardStats.inquiries?.pending || pendingInquiries,
                    contacted: serverDashboardStats.inquiries?.contacted || contactedInquiries,
                    approved: serverDashboardStats.inquiries?.approved || approvedInquiries
                },
                applications: {
                    total: serverDashboardStats.applications?.total || 0,
                    pending: serverDashboardStats.applications?.pending || 0,
                    approved: serverDashboardStats.applications?.approved || 0,
                    rejected: serverDashboardStats.applications?.rejected || 0
                },
                notifications: {
                    total: Array.isArray(notifications) ? notifications.length : 0,
                    unread: unreadNotifications
                }
            };

            setStats(dashboardStats);
            setRecentActivity(activity);
        } catch (error: any) {
            console.error('Fetch dashboard data error:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch dashboard data',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Navigation handlers for metrics cards
    const handleMetricClick = (page: string) => {
        // Map metrics to their corresponding admin routes
        const routeMap: { [key: string]: string } = {
            'users': '/admin/users',
            'companies': '/admin/companies',
            'tasks': '/admin/tasks',
            'notifications': '/admin/analytics', // Redirect to analytics for notification insights
            'inquiries': '/admin/company-inquiries',
            'task-applications': '/admin/task-applications',
            'skills': '/admin/skills',
            'analytics': '/admin/analytics'
        };

        const route = routeMap[page] || `/admin/${page}`;
        navigate(route);
    };


    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getActivityIcon = (type: string) => {
        switch (type) {
            case 'notification':
                return <BellIcon className="h-5 w-5 text-blue-500" />;
            case 'inquiry':
                return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
            case 'user_registration':
                return <UserIcon className="h-5 w-5 text-green-500" />;
            case 'task_posted':
                return <BriefcaseIcon className="h-5 w-5 text-purple-500" />;
            case 'company_verified':
                return <BuildingOfficeIcon className="h-5 w-5 text-indigo-500" />;
            case 'task_completed':
                return <ChartBarIcon className="h-5 w-5 text-yellow-500" />;
            default:
                return <ClockIcon className="h-5 w-5 text-gray-500" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'read':
            case 'completed':
            case 'active':
                return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-200';
            case 'unread':
            case 'pending':
                return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200';
            case 'inactive':
                return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-200';
            default:
                return 'text-gray-600 bg-gray-100 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="text-center">
                    <p className="text-gray-500 dark:text-gray-400">No dashboard data available</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6 lg:space-y-8 max-w-full overflow-x-hidden">
            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl sm:rounded-2xl p-4 sm:p-5 lg:p-6 text-white shadow-xl">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div className="flex-1">
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1.5 sm:mb-2 leading-tight">Admin Dashboard</h1>
                        <p className="text-blue-50 text-sm sm:text-base font-medium">
                            Welcome back, {user && (isAdmin(user) || isUser(user) || isStaff(user))
                                ? `${user.firstName} ${user.lastName}`
                                : 'System Administrator'}
                        </p>
                        <p className="text-blue-100 text-xs sm:text-sm mt-1 leading-relaxed">
                            Here's what's happening with your platform today
                        </p>
                    </div>
                    <div className="mt-2 sm:mt-0 self-start sm:self-center">
                        <div className="text-left sm:text-right">
                            <p className="text-xs sm:text-sm text-blue-100 font-medium">Last updated</p>
                            <p className="text-base sm:text-lg lg:text-xl font-bold mt-0.5">{new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-5">
                {/* Users Card */}
                <div
                    className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5 lg:p-6 transition-all duration-300 cursor-pointer hover:scale-105 active:scale-95 touch-manipulation"
                    onClick={() => handleMetricClick('users')}
                    title="Click to view user management"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1.5 sm:mb-2">Total Users</p>
                            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">{stats.users.total.toLocaleString()}</p>
                            <div className="flex items-center gap-1">
                                {stats.users.growth > 0 ? (
                                    <ArrowTrendingUpIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
                                ) : (
                                    <ArrowTrendingDownIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500 flex-shrink-0" />
                                )}
                                <span className={`text-xs sm:text-sm font-bold ${stats.users.growth > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {stats.users.growth > 0 ? '+' : ''}{stats.users.growth.toFixed(1)}%
                                </span>
                            </div>
                        </div>
                        <div className="p-2.5 sm:p-3 lg:p-3.5 bg-blue-100 dark:bg-blue-900/50 rounded-xl flex-shrink-0">
                            <UserIcon className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-blue-600 dark:text-blue-400" />
                        </div>
                    </div>
                </div>

                {/* Companies Card */}
                <div
                    className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5 lg:p-6 transition-all duration-300 cursor-pointer hover:scale-105 active:scale-95 touch-manipulation"
                    onClick={() => handleMetricClick('companies')}
                    title="Click to view company management"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1.5 sm:mb-2">Companies</p>
                            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">{stats.companies.total}</p>
                            <div className="flex items-center gap-1">
                                {stats.companies.growth > 0 ? (
                                    <ArrowTrendingUpIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
                                ) : (
                                    <ArrowTrendingDownIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500 flex-shrink-0" />
                                )}
                                <span className={`text-xs sm:text-sm font-bold ${stats.companies.growth > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {stats.companies.growth > 0 ? '+' : ''}{stats.companies.growth.toFixed(1)}%
                                </span>
                            </div>
                        </div>
                        <div className="p-2.5 sm:p-3 lg:p-3.5 bg-green-100 dark:bg-green-900/50 rounded-xl flex-shrink-0">
                            <BuildingOfficeIcon className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-green-600 dark:text-green-400" />
                        </div>
                    </div>
                </div>

                {/* Tasks Card */}
                <div
                    className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5 lg:p-6 transition-all duration-300 cursor-pointer hover:scale-105 active:scale-95 touch-manipulation"
                    onClick={() => handleMetricClick('tasks')}
                    title="Click to view task management"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1.5 sm:mb-2">Total Tasks</p>
                            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">{stats.tasks.total}</p>
                            <div className="flex items-center gap-1 flex-wrap">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">
                                    {stats.tasks.active} active, {stats.tasks.pending} pending
                                </span>
                            </div>
                        </div>
                        <div className="p-2.5 sm:p-3 lg:p-3.5 bg-purple-100 dark:bg-purple-900/50 rounded-xl flex-shrink-0">
                            <BriefcaseIcon className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-purple-600 dark:text-purple-400" />
                        </div>
                    </div>
                </div>

                {/* Notifications Card */}
                {/* <div
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl transition-shadow duration-200 cursor-pointer hover:scale-105 transform transition-all group"
                    onClick={() => handleMetricClick('notifications')}
                    title="Click to view notification analytics"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Notifications</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.notifications.total}</p>
                            <div className="flex items-center mt-2">
                                <span className="text-sm text-orange-600 dark:text-orange-400">
                                    {stats.notifications.unread} unread
                                </span>
                            </div>
                        </div>
                        <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-xl">
                            <BellIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                        </div>
                    </div>
                </div> */}

                {/* Inquiries Card */}
                <div
                    className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5 lg:p-6 transition-all duration-300 cursor-pointer hover:scale-105 active:scale-95 touch-manipulation"
                    onClick={() => handleMetricClick('inquiries')}
                    title="Click to view company inquiries"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1.5 sm:mb-2">Inquiries</p>
                            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">{stats.inquiries.total}</p>
                            <div className="flex items-center gap-1">
                                <span className="text-xs sm:text-sm text-red-600 dark:text-red-400 font-bold">
                                    {stats.inquiries.pending} pending
                                </span>
                            </div>
                        </div>
                        <div className="p-2.5 sm:p-3 lg:p-3.5 bg-red-100 dark:bg-red-900/50 rounded-xl flex-shrink-0">
                            <ExclamationTriangleIcon className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-red-600 dark:text-red-400" />
                        </div>
                    </div>
                </div>

                {/* Task Applications Card */}
                <div
                    className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5 lg:p-6 transition-all duration-300 cursor-pointer hover:scale-105 active:scale-95 touch-manipulation"
                    title="Click to view task applications"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1.5 sm:mb-2">Applications</p>
                            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">{stats.applications.total}</p>
                            <div className="flex items-center gap-1">
                                <span className="text-xs sm:text-sm text-indigo-600 dark:text-indigo-400 font-bold">
                                    {stats.applications.pending} pending
                                </span>
                            </div>
                        </div>
                        <div className="p-2.5 sm:p-3 lg:p-3.5 bg-indigo-100 dark:bg-indigo-900/50 rounded-xl flex-shrink-0">
                            <ClipboardDocumentListIcon className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-indigo-600 dark:text-indigo-400" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4 sm:p-5 lg:p-6">
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
                    <button
                        className="p-3.5 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all duration-200 text-left active:scale-95 touch-manipulation hover:shadow-md"
                        onClick={() => handleMetricClick('users')}
                    >
                        <UserIcon className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600 dark:text-blue-400 mb-2" />
                        <h4 className="font-bold text-sm sm:text-base text-gray-900 dark:text-white mb-1">Manage Users</h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-snug">View and manage all users</p>
                    </button>
                    <button
                        className="p-3.5 sm:p-4 bg-green-50 dark:bg-green-900/20 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/40 transition-all duration-200 text-left active:scale-95 touch-manipulation hover:shadow-md"
                        onClick={() => handleMetricClick('companies')}
                    >
                        <BuildingOfficeIcon className="h-7 w-7 sm:h-8 sm:w-8 text-green-600 dark:text-green-400 mb-2" />
                        <h4 className="font-bold text-sm sm:text-base text-gray-900 dark:text-white mb-1">Manage Companies</h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-snug">View and manage companies</p>
                    </button>
                    <button
                        className="p-3.5 sm:p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-all duration-200 text-left active:scale-95 touch-manipulation hover:shadow-md"
                        onClick={() => handleMetricClick('tasks')}
                    >
                        <BriefcaseIcon className="h-7 w-7 sm:h-8 sm:w-8 text-purple-600 dark:text-purple-400 mb-2" />
                        <h4 className="font-bold text-sm sm:text-base text-gray-900 dark:text-white mb-1">Manage Tasks</h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-snug">View and manage task postings</p>
                    </button>
                    <button
                        className="p-3.5 sm:p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl hover:bg-yellow-100 dark:hover:bg-yellow-900/40 transition-all duration-200 text-left active:scale-95 touch-manipulation hover:shadow-md"
                        onClick={() => handleMetricClick('analytics')}
                    >
                        <ChartBarIcon className="h-7 w-7 sm:h-8 sm:w-8 text-yellow-600 dark:text-yellow-400 mb-2" />
                        <h4 className="font-bold text-sm sm:text-base text-gray-900 dark:text-white mb-1">View Analytics</h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-snug">Detailed platform analytics</p>
                    </button>
                    <button
                        className="p-3.5 sm:p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-all duration-200 text-left active:scale-95 touch-manipulation hover:shadow-md"
                        onClick={() => handleMetricClick('skills')}
                    >
                        <AcademicCapIcon className="h-7 w-7 sm:h-8 sm:w-8 text-indigo-600 dark:text-indigo-400 mb-2" />
                        <h4 className="font-bold text-sm sm:text-base text-gray-900 dark:text-white mb-1">Manage Skills</h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-snug">Platform skills and categories</p>
                    </button>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4 sm:p-5 lg:p-6">
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">Recent Activity</h3>
                <div className="space-y-2.5 sm:space-y-3">
                    {recentActivity.length > 0 ? (
                        recentActivity.map((activity) => (
                            <div key={activity.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
                                    <div className="flex-shrink-0">
                                        {getActivityIcon(activity.type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white line-clamp-2">
                                            {activity.description}
                                        </p>
                                        {activity.user && (
                                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                                                by {activity.user}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap justify-between sm:justify-end">
                                    {activity.status && (
                                        <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${getStatusColor(activity.status)}`}>
                                            {activity.status}
                                        </span>
                                    )}
                                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
                                        {formatDate(activity.timestamp)}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-6 sm:py-8">
                            <ClockIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 font-medium">No recent activity</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminPanel;
